import { NbAuthModule } from '@nebular/auth';
import { ShellModule } from './shared/shell/shell.module';
import { environment } from './../environments/environment';
import { SharedModule } from './shared/shared.module';
import { NgModule, ErrorHandler, APP_ID, inject, NgZone, PLATFORM_ID } from '@angular/core';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  NbThemeModule,
  NbSidebarModule,
  NbMenuModule,
  NbDatepickerModule,
  NbDialogModule,
  NbToastrModule,
  NbWindowModule
} from '@nebular/theme';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { FirebaseAuthInterceptor } from './interceptors/firebase-auth.interceptor';
import { NgxSpinnerModule } from 'ngx-spinner';
import { NotFoundGuard } from './shared/404/not-found.guard';
import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { getRemoteConfig, provideRemoteConfig } from '@angular/fire/remote-config';
import { getStorage, provideStorage } from '@angular/fire/storage';
import { initializeAnalytics, provideAnalytics } from '@angular/fire/analytics';
import { providePerformance, getPerformance } from '@angular/fire/performance';
import { provideFunctions, getFunctions } from '@angular/fire/functions';

import { provideAppCheck, initializeAppCheck, ReCaptchaV3Provider } from '@angular/fire/app-check';
import { isPlatformServer } from '@angular/common';
import { MarkdownModule } from 'ngx-markdown';
import { GlobalErrorHandler } from './global-error-handler';

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserAnimationsModule,
    AppRoutingModule,
    HttpClientModule,
    NbThemeModule.forRoot({ name: 'default' }),
    NbSidebarModule.forRoot(),
    NbMenuModule.forRoot(),
    NbDatepickerModule.forRoot(),
    NbDialogModule.forRoot(),
    NbWindowModule.forRoot(),
    NbToastrModule.forRoot(),
    NbAuthModule.forRoot(),
    NgxSpinnerModule,
    ShellModule,
    SharedModule,
    MarkdownModule.forRoot()
  ],
  providers: [
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideFirestore(() => getFirestore()),
    provideAuth(() => getAuth()),
    provideFunctions(() => getFunctions()),
    provideRemoteConfig(() => {
      const r = getRemoteConfig();
      r.settings.minimumFetchIntervalMillis = environment.production ? 300000 : 5000;
      return r;
    }),
    provideStorage(() => getStorage()),
    provideAnalytics(() => {
      const a = getApp();
      const anal = initializeAnalytics(a, {
        config: {
          send_page_view: true,
          allow_ad_personalization_signals: true,
          anonymize_ip: true
        }
      });
      anal.app.automaticDataCollectionEnabled = true;
      return anal;
    }),
    providePerformance(() => {
      const p = getPerformance();
      p.dataCollectionEnabled = true;
      p.instrumentationEnabled = true;
      return p;
    }),
    provideAppCheck(() => {
      const platformId = inject(PLATFORM_ID);
      if (isPlatformServer(platformId)) {
        return;
      }
      const a = getApp();
      // Run outside Angular so Zone.js doesn't patch reCAPTCHA's internal timers —
      // otherwise NgZone ticks queued reCAPTCHA callbacks after its state has been
      // torn down (e.g. mid-navigation), producing TypeErrors from gstatic.
      const ngZone = inject(NgZone);
      return ngZone.runOutsideAngular(() => {
        return initializeAppCheck(a, {
          provider: new ReCaptchaV3Provider(environment.recaptchaSiteKeyV3),
          isTokenAutoRefreshEnabled: true
        });
      });
    }),
    {
      provide: APP_ID,
      useValue: 'serverApp'
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler
    },
    { provide: HTTP_INTERCEPTORS, useClass: FirebaseAuthInterceptor, multi: true },
    NotFoundGuard
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
